/**
 * Sentry integration module
 * Handles error tracking and performance monitoring in production only
 */

import * as Sentry from '@sentry/react'

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD
}

/**
 * Initialize Sentry
 * Only initializes in production mode
 */
export function initSentry() {
  // Only initialize Sentry in production
  if (!isProduction()) {
    console.log('[Sentry Dev] Sentry disabled in development mode')
    return
  }

  Sentry.init({
    dsn: 'https://1b7c0e64e7bc6dce915d37fcdc8c2602@o4510688935084032.ingest.us.sentry.io/4510857506455552',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // Adjust this value in production
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control what URLs distributed tracing should be enabled for
    tracePropagationTargets: ['localhost', /^https:\/\/yourapp\.io\/api/],
    // Enable logging support
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Automatically capture console.log, console.warn, and console.error as logs
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Session Replay sample rate
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  })

  console.log('[Sentry] Initialized in production mode')
}

// Export the logger for structured logging
const { logger } = Sentry

export { logger }

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (!isProduction()) {
    console.log('[Sentry Dev] Would capture exception:', error, context)
    return
  }

  if (context) {
    Sentry.captureException(error, { extra: context })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
) {
  if (!isProduction()) {
    console.log('[Sentry Dev] Would capture message:', message, level)
    return
  }

  Sentry.captureMessage(message, level)
}

/**
 * Set user context for Sentry
 */
export function setUser(user: { email: string; id: string } | null) {
  if (!isProduction()) {
    console.log('[Sentry Dev] Would set user:', user)
    return
  }

  if (user) {
    Sentry.setUser({
      email: user.email,
      id: user.id,
    })
  } else {
    Sentry.setUser(null)
  }
}

/**
 * Add custom context/tags to Sentry events
 */
export function setContext(key: string, value: Record<string, any>) {
  if (!isProduction()) {
    console.log('[Sentry Dev] Would set context:', key, value)
    return
  }

  Sentry.setContext(key, value)
}

/**
 * Create a custom span for tracking performance
 * Example: startSpan({ op: 'http.client', name: 'GET /api/users' }, async () => { ... })
 */
export function startSpan<T>(
  options: { op: string; name: string; attributes?: Record<string, any> },
  callback: (span: Sentry.Span) => T,
): T {
  if (!isProduction()) {
    console.log('[Sentry Dev] Would start span:', options)
    return callback({} as Sentry.Span)
  }

  return Sentry.startSpan(options, (span) => {
    if (options.attributes) {
      Object.entries(options.attributes).forEach(([key, value]) => {
        span.setAttribute(key, value)
      })
    }
    return callback(span)
  })
}

// Re-export Sentry for direct usage when needed
export { Sentry }
